import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { User } from '@/types/auth';
import { getUserOrganizationId, useStations } from '../hooks/useStationAssets';
import { SelectedStationContext } from './SelectedStationContextValue';

const storageKeyPrefix = 'fuelops.selected_station_id';

const resolveUserStationId = (station: User['station']) => {
  if (station && typeof station === 'object' && 'id' in station) {
    return String(station.id);
  }

  if (station !== null && station !== undefined && typeof station !== 'object') {
    return String(station);
  }

  return '';
};

export const SelectedStationProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const { data: stations = [] } = useStations();
  const userStationId = resolveUserStationId(user?.station);
  const storageKey = `${storageKeyPrefix}.${organizationId ?? 'none'}.${user?.id ?? 'anonymous'}`;
  const [selectedStationId, setSelectedStationId] = useState('');

  useEffect(() => {
    if (!user) {
      setSelectedStationId('');
      return;
    }

    if (userStationId) {
      setSelectedStationId(userStationId);
      return;
    }

    setSelectedStationId(window.localStorage.getItem(storageKey) ?? '');
  }, [storageKey, user, userStationId]);

  useEffect(() => {
    if (userStationId) {
      return;
    }

    if (!selectedStationId) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, selectedStationId);
  }, [selectedStationId, storageKey, userStationId]);

  useEffect(() => {
    if (!selectedStationId || stations.length === 0) {
      return;
    }

    const availableStations = userStationId
      ? stations.filter((station) => String(station.id) === userStationId)
      : stations;
    const selectedStillAvailable = availableStations.some(
      (station) => String(station.id) === selectedStationId
    );

    if (!selectedStillAvailable) {
      setSelectedStationId('');
    }
  }, [selectedStationId, stations, userStationId]);

  useEffect(() => {
    const onlyStation = stations.length === 1 ? stations[0] : undefined;
    if (userStationId || selectedStationId || !onlyStation) {
      return;
    }

    setSelectedStationId(String(onlyStation.id));
  }, [selectedStationId, stations, userStationId]);

  const availableStations = userStationId
    ? stations.filter((station) => String(station.id) === userStationId)
    : stations;
  const selectedStation = availableStations.find(
    (station) => String(station.id) === selectedStationId
  );

  const value = useMemo(
    () => ({
      organizationId,
      selectedStationId,
      selectedStation,
      availableStations,
      stationLocked: Boolean(userStationId),
      setSelectedStationId: userStationId
        ? () => setSelectedStationId(userStationId)
        : setSelectedStationId,
      clearSelectedStation: () => setSelectedStationId('')
    }),
    [availableStations, organizationId, selectedStation, selectedStationId, userStationId]
  );

  return (
    <SelectedStationContext.Provider value={value}>{children}</SelectedStationContext.Provider>
  );
};
